import { Grid, TableCell, TableHead, TableRow } from "@material-ui/core";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import { Close, Delete, Done } from "@material-ui/icons";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import moment from "moment-timezone";
import { useCallback } from "react";
import { CloneButton, Datagrid, EditButton, Link, TopToolbar } from "react-admin";
import { PopupboxContainer, PopupboxManager } from "react-popupbox";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";

export const defaultPlatformColumns = [
  { key: "stb", label: "STB", id: "" },
  { key: "android_tv", label: "Android TV", id: "" },
  { key: "samsung_tv", label: "Samsung TV", id: "" },
  { key: "lg_tv", label: "LG TV", id: "" },
  { key: "ios", label: "iOS", id: "" },
  { key: "android", label: "Android", id: "" },
  { key: "web", label: "Web", id: "" },
];

export const validateMpmRowsGridPlatformOrders = (values) => {
  const errors = {};
  const platformOrders = values.platform_orders || {};

  for (const { key, label } of defaultPlatformColumns) {
    const seenOrders = {};

    for (const rowId of Object.keys(platformOrders)) {
      const value = platformOrders[rowId]?.[key];
      if (value == null || String(value).trim() === "") {
        continue;
      }

      const numericValue = Number(value);
      if (!/^\d+(\.\d+)?$/.test(String(value).trim()) || !Number.isFinite(numericValue) || numericValue <= 0) {
        errors.platform_orders = `${label} platform order must be a positive number or empty`;
        return errors;
      }

      const normalizedValue = String(numericValue);
      if (seenOrders[normalizedValue]) {
        errors.platform_orders = `Duplicate ${label} platform order: ${normalizedValue}`;
        return errors;
      }

      seenOrders[normalizedValue] = true;
    }
  }

  return errors;
};

const DragHandle = SortableHandle(() => (
  <TableCell>
    <ListItemIcon>
      <DragHandleIcon />
    </ListItemIcon>
  </TableCell>
));

const SortableItem = SortableElement(({ pageId, profileClass, isHomepage, item, layoutName, form, onDelete, showCloneButton, showDeleteButton, showPlatformOrders, showReorderButton, showReorderColumn, platformColumns }) => {
  return (
    <MpmRowsGridRow //
      pageId={pageId}
      profileClass={profileClass}
      isHomepage={isHomepage}
      item={item}
      layoutName={layoutName}
      showCloneButton={showCloneButton}
      showDeleteButton={showDeleteButton}
      showPlatformOrders={showPlatformOrders}
      showReorderButton={showReorderButton}
      showReorderColumn={showReorderColumn}
      form={form}
      onDelete={onDelete}
      platformColumns={platformColumns}
    />
  );
});

const MpmRowsGridHeader = ({ platformColumns, showActions, showPlatformLinks, showPlatformOrders, showReorderColumn }) => (
  <TableHead>
    <TableRow>
      {showReorderColumn && <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "50px" }}></TableCell>}
      <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "200px" }}>
        ID
      </TableCell>
      <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "200px" }}>
        Row Name
      </TableCell>
      <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "200px" }}>
        Title in TC
      </TableCell>
      <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "200px" }}>
        Content
      </TableCell>
      <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "200px" }}>
        Layout
      </TableCell>
      <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "200px" }}>
        RowPath
      </TableCell>
      <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "200px" }}>
        Onshelf Datetime
      </TableCell>
      <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "200px" }}>
        Offshelf Datetime
      </TableCell>
      {showPlatformOrders && (
        <TableCell align="center" colSpan={platformColumns.length}>
          Platform
        </TableCell>
      )}
      {showActions && <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "50px" }}></TableCell>}
      {showActions && <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "50px" }}></TableCell>}
      {showActions && <TableCell rowSpan={showPlatformOrders ? 2 : 1} style={{ width: "50px" }}></TableCell>}
    </TableRow>
    {showPlatformOrders && (
      <TableRow>
        {platformColumns.map((platformColumn) => (
          <TableCell key={platformColumn.key} align="center" style={{ width: "70px", minWidth: "70px", maxWidth: "70px", padding: "6px" }}>
            {showPlatformLinks && platformColumn.id ? <Link to={{ pathname: `/mpm-rows-by-platform/${platformColumn.id}`, state: { _scrollToTop: true } }}>{platformColumn.label}</Link> : platformColumn.label}
          </TableCell>
        ))}
      </TableRow>
    )}
  </TableHead>
);

const MpmRowsGridBody = ({ pageId, profileClass, isHomepage, templates, items, form, onDelete, showCloneButton, showDeleteButton, showPlatformOrders, showReorderButton, platformColumns }) => {
  return items.map((item, index) => {
    var layoutName = "";
    for (const template of templates) {
      if (template.id == item.template) {
        layoutName = template.name;
      }
    }

    const canReorder = showReorderButton && !(isHomepage && index == 0);
    const canDelete = showDeleteButton && !(isHomepage && index == 0);

    if (!canReorder) {
      return (
        <MpmRowsGridRow //
          key={item.id}
          pageId={pageId}
          profileClass={profileClass}
          isHomepage={isHomepage}
          item={item}
          layoutName={layoutName}
          showCloneButton={showCloneButton}
          showDeleteButton={canDelete}
          showPlatformOrders={showPlatformOrders}
          showReorderButton={canReorder}
          showReorderColumn={showReorderButton}
          form={form}
          onDelete={onDelete ? () => onDelete(item.id) : undefined}
          platformColumns={platformColumns}
        />
      );
    }

    return (
      <SortableItem //
        key={item.id}
        pageId={pageId}
        profileClass={profileClass}
        isHomepage={isHomepage}
        index={index}
        item={item}
        layoutName={layoutName}
        form={form}
        onDelete={onDelete ? () => onDelete(item.id) : undefined}
        showCloneButton={showCloneButton}
        showDeleteButton={canDelete}
        showPlatformOrders={showPlatformOrders}
        showReorderButton={canReorder}
        showReorderColumn={showReorderButton}
        platformColumns={platformColumns}
      />
    );
  });
};

const CustomCloneButton = ({ pageId, profileClass, isHomepage, item }) => {
  const onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    var url = `/#/mpm-rows/create?page_id=${pageId}&profile_class=${profileClass}&is_homepage=${isHomepage}&source=`;
    const model = JSON.parse(JSON.stringify(item));
    model.platform_with_order = Object.entries(model.platform_orders || {})
      .filter(([, order]) => order !== undefined && order !== null && String(order).trim() !== "")
      .map(([platform, order]) => ({ platform, order }));
    url += encodeURIComponent(JSON.stringify(model));

    window.location.href = url;
  };

  return <CloneButton onClick={onClick} />;
};

const MpmRowsGridRow = ({ pageId, profileClass, isHomepage, item, layoutName, showCloneButton, showDeleteButton, showPlatformOrders, showReorderButton, showReorderColumn, form, onDelete, platformColumns }) => {
  var content = "";
  if (item.type == "editorial_pick") {
    content = "Editorial";
  } else if (item.type == "event") {
    content = "Event";
  } else if (item.type == "programme_list") {
    content = "ProgrammeList";
  } else if (item.type == "history") {
    content = "WatchingHistory";
  } else if (item.type == "recom_personalised") {
    content = "Recommendation (Personalised)";
  } else if (item.type == "recom_trendy") {
    content = "Recommendation (Trending)";
  } else if (item.type == "recom_universal") {
    content = "Recommendation (Universal list)";
  } else if (item.type == "latest") {
    content = "Latest";
  } else if (item.type == "top_sv") {
    content = "Top SV";
  } else if (item.type == "episode_list") {
    content = "Episode";
  } else if (item.type == "clip_list") {
    content = "Clips";
  } else if (item.type == "category_list") {
    content = "Category List";
  } else if (item.type == "recom_free_preview") {
    content = "Free Episode";
  } else {
    content = item.type;
  }

  var onshelfTime = "";
  var offshelfTime = "";
  if (item.start_time) {
    onshelfTime = moment.tz(item.start_time, "Asia/Hong_Kong").format("YYYY-MM-DD HH:mm");
  }
  if (item.end_time) {
    offshelfTime = moment.tz(item.end_time, "Asia/Hong_Kong").format("YYYY-MM-DD HH:mm");
  }

  const rowDeletePopupContent = (
    <div style={{ textAlign: "center" }}>
      <Grid item sm={12} md={12}>
        <h2>Delete Row</h2>
      </Grid>
      <Grid item sm={12} md={12}>
        <br />
        <p>{"Are you sure?"}</p>
        <br />
      </Grid>
      <Grid item sm={12} md={12}>
        <Grid sm={12} md={12} container justifyContent="space-around">
          <EditButton
            label="Confirm"
            icon={<Done />}
            onClick={() => {
              onDelete();
              PopupboxManager.close();
            }}
          />

          <EditButton
            label="Cancel"
            icon={<Close />}
            onClick={() => {
              PopupboxManager.close();
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
  const rowDeleteOnClick = useCallback(() => {
    PopupboxManager.open({ content: rowDeletePopupContent });
  });

  return (
    <TableRow>
      {showReorderColumn && (showReorderButton ? <DragHandle /> : <TableCell />)}
      <TableCell>{item.id}</TableCell>
      <TableCell>{item.name}</TableCell>
      <TableCell>{item.title_tc}</TableCell>
      <TableCell>{content}</TableCell>
      <TableCell>{layoutName}</TableCell>
      <TableCell>{item.path}</TableCell>
      <TableCell>{onshelfTime}</TableCell>
      <TableCell>{offshelfTime}</TableCell>
      {showPlatformOrders &&
        platformColumns.map(({ key }) => (
          <TableCell key={key} align="center" style={{ width: "70px", minWidth: "70px", maxWidth: "70px", padding: "6px" }}>
            <input
              type="text"
              defaultValue={item.platform_orders?.[key] ?? ""}
              onChange={(event) => {
                form.change(`platform_orders.${item.id}.${key}`, event.target.value);
              }}
              style={{ width: "100%", boxSizing: "border-box", textAlign: "center" }}
            />
          </TableCell>
        ))}
      <TableCell>
        <EditButton basePath="/mpm-rows" label="Edit" record={{ id: item.id }} />
      </TableCell>
      {showCloneButton ? (
        <TableCell>
          <CustomCloneButton pageId={pageId} profileClass={profileClass} isHomepage={isHomepage} item={item} />
        </TableCell>
      ) : (
        <TableCell />
      )}

      {showDeleteButton ? (
        <TableCell>
          <EditButton label="DELETE" icon={<Delete />} onClick={rowDeleteOnClick} style={{ color: "red" }} />
          <PopupboxContainer />
        </TableCell>
      ) : (
        <TableCell />
      )}
    </TableRow>
  );
};

const SortableListContainer = SortableContainer(({ ...props }) => {
  const { pageId, profileClass, isHomepage, templates, items, form, onDelete, showCloneButton, showDeleteButton, showPlatformOrders, showPlatformLinks, showReorderButton, platformColumns } = props;
  const showActions = true;

  return (
    <Datagrid //
      style={{ tableLayout: "auto", minWidth: showPlatformOrders ? "2290px" : "1650px" }}
      header={<MpmRowsGridHeader platformColumns={platformColumns} showActions={showActions} showPlatformLinks={showPlatformLinks} showPlatformOrders={showPlatformOrders} showReorderColumn={showReorderButton} />}
      body={
        <MpmRowsGridBody //
          pageId={pageId}
          isHomepage={isHomepage}
          profileClass={profileClass}
          templates={templates}
          items={items}
          form={form}
          onDelete={onDelete}
          showCloneButton={showCloneButton}
          showDeleteButton={showDeleteButton}
          showPlatformOrders={showPlatformOrders}
          showReorderButton={showReorderButton}
          platformColumns={platformColumns}
        />
      }
    ></Datagrid>
  );
});

export const MpmRowsGrid = ({
  profileClass,
  pageId,
  isHomepage,
  templates,
  rows,
  form,
  onSortEnd,
  onDelete,
  platformColumns = defaultPlatformColumns,
  showCreateButton = true,
  showCloneButton = true,
  showDeleteButton = true,
  showPlatformLinks = true,
  showPlatformOrders = true,
  showReorderButton = true,
}) => {
  return (
    <Grid container spacing={1} fullWidth>
      <Grid item sm={12} md={12}>
        {showCreateButton && (
          <TopToolbar>
            <Link to={`/mpm-rows/create?page_id=${pageId}&profile_class=${profileClass}&is_homepage=${isHomepage}`}>Create</Link>
          </TopToolbar>
        )}
        <SortableListContainer //
          pageId={pageId}
          profileClass={profileClass}
          isHomepage={isHomepage}
          templates={templates}
          items={rows}
          form={form}
          onSortEnd={onSortEnd}
          onDelete={onDelete}
          useDragHandle={true}
          lockAxis="y"
          showCloneButton={showCloneButton}
          showDeleteButton={showDeleteButton}
          showPlatformLinks={showPlatformLinks}
          showPlatformOrders={showPlatformOrders}
          showReorderButton={showReorderButton}
          platformColumns={platformColumns}
        />
      </Grid>
    </Grid>
  );
};
